/**
 * REST heartbeat reporter — POSTs the player's liveness to the CMS
 * `/api/heartbeat` endpoint on a fixed interval (default 60 s).
 *
 * The CMS marks a screen ONLINE only through this REST endpoint
 * (`heartbeat.service.ts` sets status=ONLINE + lastHeartbeatAt); the MQTT
 * telemetry topic has no server-side consumer. This hook therefore runs
 * unconditionally while the player is paired — independent of the MQTT
 * connection state — so the dashboard reflects reality even when MQTT is
 * down (which is itself worth surfacing as "online but degraded" later).
 *
 * Mirrors the reporter patterns in `usePlayLog` / `useInteractionReporter`:
 * refs for mutable state, interval + unmount cleanup, dev-gated logging.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { CMS_API_BASE } from '../types';
import type { RecycleHeartbeatBlock } from '../types';

/** Heartbeat interval in milliseconds (60 s — matches the CMS rate limit). */
const HEARTBEAT_INTERVAL_MS = 60 * 1000;

export interface UseRestHeartbeatOptions {
  deviceId: string;
  /** JWT bearer token for authenticating with the CMS heartbeat API. */
  jwt: string;
  /** Optional: interval override in ms (default: 60000 = 60 s). */
  intervalMs?: number;
}

/** Playback fields carried in the heartbeat, updated by the playback engine. */
export interface HeartbeatPlaybackInfo {
  currentAssetId?: string | null;
  scheduleVersion?: string | null;
}

/**
 * Recycle-kiosk health block carried in the REST heartbeat, matching
 * `heartbeatInputSchema.recycle` (packages/shared/src/validation.ts).
 *
 * This is the CMS's authoritative source for recycle fleet health: the same
 * block was previously only published over the MQTT telemetry topic, which
 * has no server-side consumer (see file header). Extends the base
 * `RecycleHeartbeatBlock` (types.ts) with the v2 additive fields mirrored
 * from the agent's `agentStatusHealthSchema` (recycle-contract.ts) —
 * `catalogVersion`/`outboxDepth` are defined ahead of the agent actually
 * producing them, so they're passed through opportunistically when present.
 */
export interface HeartbeatRecycleInfo extends RecycleHeartbeatBlock {
  /** Agent operational state (e.g. "OUT_OF_SERVICE", "OBSTRUCTED"). */
  state?: string;
  /** Present only while state === "OUT_OF_SERVICE". */
  outOfServiceReason?: 'bin_full' | 'hardware_fault';
  /** Agent-side barcode catalog cache version, once the agent exposes it. */
  catalogVersion?: number;
  /** Agent-side offline-verdict outbox depth, once the agent exposes it. */
  outboxDepth?: number;
}

export interface UseRestHeartbeatReturn {
  /** Update the playback fields included in subsequent heartbeats. */
  updatePlayback: (info: HeartbeatPlaybackInfo) => void;
  /**
   * Replace the recycle health snapshot included in subsequent heartbeats.
   * Full replacement (not merged) — each call is the latest known agent
   * status, so a recovered field (e.g. `state` clearing on an OUT_OF_SERVICE
   * -> IDLE transition) can't linger from a stale partial update.
   */
  updateRecycle: (info: HeartbeatRecycleInfo) => void;
}

function log(...args: unknown[]) {
  // Dev-only: silent in production kiosk builds.
  if (import.meta.env.DEV) console.log('[RestHeartbeat]', ...args);
}

/**
 * React hook that reports player liveness to the CMS over REST.
 */
export function useRestHeartbeat(
  options: UseRestHeartbeatOptions,
): UseRestHeartbeatReturn {
  const { deviceId, jwt, intervalMs = HEARTBEAT_INTERVAL_MS } = options;

  const playbackRef = useRef<HeartbeatPlaybackInfo>({});
  // null until the first updateRecycle() call — screens with no recycle
  // agent never call it, so `recycle` stays entirely absent from the
  // payload rather than sending an empty/placeholder object.
  const recycleRef = useRef<HeartbeatRecycleInfo | null>(null);
  const inFlightRef = useRef(false);

  const sendHeartbeat = useCallback(async () => {
    // Never stack a second request behind a hung one — skip this tick.
    if (inFlightRef.current) {
      log('Previous heartbeat still in flight; skipping tick');
      return;
    }
    inFlightRef.current = true;

    // heartbeatInputSchema only permits null on currentAssetId; every other
    // optional field must be omitted when unknown.
    const { currentAssetId, scheduleVersion } = playbackRef.current;
    const payload: Record<string, unknown> = {
      deviceId,
      timestamp: new Date().toISOString(),
      status: 'online',
      currentAssetId: currentAssetId ?? null,
    };
    if (scheduleVersion) {
      payload.scheduleVersion = scheduleVersion;
    }
    if (recycleRef.current) {
      payload.recycle = recycleRef.current;
    }

    try {
      const response = await fetch(`${CMS_API_BASE}/api/heartbeat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        log('Heartbeat sent');
      } else {
        const errorText = await response.text().catch(() => 'Unknown error');
        log(`Heartbeat rejected: ${response.status} ${errorText}`);
      }
    } catch (err) {
      log(`Heartbeat network error: ${err instanceof Error ? err.message : err}`);
    } finally {
      inFlightRef.current = false;
    }
  }, [deviceId, jwt]);

  const updatePlayback = useCallback((info: HeartbeatPlaybackInfo) => {
    playbackRef.current = { ...playbackRef.current, ...info };
  }, []);

  const updateRecycle = useCallback((info: HeartbeatRecycleInfo) => {
    // Full replace, not merge — see UseRestHeartbeatReturn.updateRecycle doc.
    recycleRef.current = info;
  }, []);

  // ─── Send immediately on mount, then on the interval ────────────────
  useEffect(() => {
    void sendHeartbeat();
    const timer = setInterval(() => {
      void sendHeartbeat();
    }, intervalMs);

    return () => {
      clearInterval(timer);
    };
  }, [sendHeartbeat, intervalMs]);

  // Stable identity so consumers don't re-run effects every render.
  return useMemo(
    () => ({ updatePlayback, updateRecycle }),
    [updatePlayback, updateRecycle],
  );
}
